import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { environment } from "@web-src/environments/environment";
import { WhatsappAccountContext } from "./WhatsappAccountContext";
import { useManageAccountsDrawer } from "../../components/ManageAccountsDrawer/providers/ManageAccountsDrawerContext";
import { extractNonEmptyAccount } from "../../utils/extractNonEmptyAccount";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const FB: any;

interface WhatsappAccountProviderProps {
  children: ReactNode;
}

export const WhatsappAccountProvider: React.FC<
  WhatsappAccountProviderProps
> = ({ children }) => {
  const { integrationsApi } = useRestApiProvider();
  const [createWhatsappAgentAccount, { isLoading: isCreatingAccount }] =
    integrationsApi.useConnectWhatsappAgentAccountMutation();
  const [registerWhatsappAccount, { isLoading: isRegisteringAccount }] =
    integrationsApi.useRegisterWhatsappAgentAccountMutation();
  const { entityId } = useEntitySelectedProvider();
  const { crmConfig } = useManageAccountsDrawer();

  const [loginInfo, setLoginInfo] = useState<{
    wabaId?: string;
    phoneNumberId?: string;
    code?: string;
  }>({});
  const [isLoginInProgress, setIsLoginInProgress] = useState(false);

  const whatsappAgent = useMemo(
    () => extractNonEmptyAccount(crmConfig?.whatsapp),
    [crmConfig?.whatsapp]
  );

  const isConnected = useMemo(
    () => whatsappAgent?.phone_number.status === "CONNECTED",
    [whatsappAgent?.phone_number.status]
  );

  useEffect(() => {
    if (loginInfo.code && loginInfo.phoneNumberId && loginInfo.wabaId) {
      createWhatsappAgentAccount({
        entityId,
        data: {
          code: loginInfo.code,
          phone_number_id: loginInfo.phoneNumberId,
          waba_id: loginInfo.wabaId,
        },
      });
      setLoginInfo({});
    }
  }, [loginInfo, createWhatsappAgentAccount, entityId]);

  const connectWhatsappAccount = useCallback(async () => {
    const originalPathname = window.location.pathname;
    setIsLoginInProgress(true);
    window.history.replaceState({}, "", "/");
    await FB.login(
      ({
        authResponse,
        status,
      }: {
        authResponse?: { code: string };
        status: "connected";
      }) => {
        setIsLoginInProgress(false);
        if (status === "connected" && authResponse?.code) {
          setLoginInfo((prev) => ({ ...prev, code: authResponse.code }));
        }
      },
      {
        config_id: environment.facebookConfigId,
        response_type: "code",
        override_default_response_type: true,
        display: "popup",
        extras: {
          setup: {},
          featureType: "",
          sessionInfoVersion: "2",
        },
      }
    );
    window.history.replaceState({}, "", originalPathname);
  }, []);

  const reconnectWhatsappAccount = useCallback(async () => {
    await registerWhatsappAccount({ entityId });
  }, [registerWhatsappAccount, entityId]);

  useEffect(() => {
    const eventCB = (event: MessageEvent) => {
      if (
        event.origin !== "https://www.facebook.com" &&
        event.origin !== "https://web.facebook.com"
      ) {
        return;
      }
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const data = JSON.parse(event.data) as any;
        if (data.type === "WA_EMBEDDED_SIGNUP") {
          // if user finishes the Embedded Signup flow
          if (data.event === "FINISH") {
            const { phone_number_id: phoneNumberId, waba_id: wabaId } =
              data.data;
            setLoginInfo((prev) => ({ ...prev, phoneNumberId, wabaId }));
            // if user cancels the Embedded Signup flow
          } else if (data.event === "CANCEL") {
            setIsLoginInProgress(false);
          } else if (data.event === "ERROR") {
            setIsLoginInProgress(false);
          }
        }
      } catch {
        // do nothing
      }
    };
    window.addEventListener("message", eventCB);
    return () => {
      window.removeEventListener("message", eventCB);
    };
  }, []);

  const contextValue = useMemo(
    () => ({
      whatsappAgent,
      isConnected,

      connectWhatsappAccount,
      reconnectWhatsappAccount,

      isLoginInProgress,
      isCreatingAccount,
      isRegisteringAccount,
    }),
    [
      whatsappAgent,
      isConnected,
      connectWhatsappAccount,
      reconnectWhatsappAccount,
      isLoginInProgress,
      isCreatingAccount,
      isRegisteringAccount,
    ]
  );

  return (
    <WhatsappAccountContext.Provider value={contextValue}>
      {children}
    </WhatsappAccountContext.Provider>
  );
};
